import { GLOBAL_SEARCH_MIN_QUERY_LENGTH } from "../constants/global-search";
import {
  EGlobalSearchItemType,
  EGlobalSearchTargetKind,
  type IGlobalSearchItem,
} from "../types/global-search";
import type {
  IBuildGlobalSearchActionItemsProps,
  IBuildGlobalSearchFaqItemsProps,
  IBuildGlobalSearchIntegrationProviderItemsProps,
  IBuildGlobalSearchOrganizationItemsProps,
  IBuildGlobalSearchProjectItemsProps,
  IFindFaqItemByDeepLinkIdProps,
} from "../types/global-search-client";
import type { IFaqItem } from "../types/help";
import { scoreGlobalSearchMatch } from "./global-search";

interface IScoredItem {
  item: IGlobalSearchItem;
  score: number;
}

const rankItems = (
  scored: readonly IScoredItem[],
  limit: number,
): IGlobalSearchItem[] =>
  // NOTE: In-place `sort` on the fresh array `filter` returns — mobile's Hermes
  // has no Array.prototype.toSorted (see sortGlobalSearchGroups).
  scored
    .filter((entry) => entry.score > 0)
    .sort((left, right) => right.score - left.score)
    .slice(0, limit)
    .map((entry) => entry.item);

const isQuerySearchable = (query: string): boolean =>
  query.trim().length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH;

// NOTE: NSAI-6605 — every project the user can switch to, the personal one
// included. The organization name is a secondary candidate so "acme" lists all
// of Acme's projects.
export const buildGlobalSearchProjectItems = ({
  query,
  projects,
  currentProjectUuid,
  personalLabel,
  limit,
}: IBuildGlobalSearchProjectItemsProps): IGlobalSearchItem[] => {
  if (!projects || !isQuerySearchable(query)) return [];

  const personal = projects.personalProject;
  const candidates = [
    ...(personal
      ? [{ uuid: personal.uuid, title: personal.title, owner: personalLabel }]
      : []),
    ...projects.organizationProjects.flatMap((organization) =>
      organization.projects.map((project) => ({
        uuid: project.uuid,
        title: project.title,
        owner: organization.name,
      })),
    ),
  ].filter((candidate) => candidate.uuid !== currentProjectUuid);

  return rankItems(
    candidates.map((candidate) => ({
      item: {
        type: EGlobalSearchItemType.PROJECT,
        id: candidate.uuid,
        title: candidate.title,
        subtitle: candidate.owner,
        params: {},
      },
      score: scoreGlobalSearchMatch({
        query,
        candidates: [candidate.title, candidate.owner],
      }),
    })),
    limit,
  );
};

// NOTE: NSAI-6605 — the organizations the user belongs to; opening one lands
// on its settings.
export const buildGlobalSearchOrganizationItems = ({
  query,
  projects,
  limit,
}: IBuildGlobalSearchOrganizationItemsProps): IGlobalSearchItem[] => {
  if (!projects || !isQuerySearchable(query)) return [];

  return rankItems(
    projects.organizationProjects.map((organization) => ({
      item: {
        type: EGlobalSearchItemType.ORGANIZATION,
        id: organization.uuid,
        title: organization.name,
        subtitle: null,
        params: {},
      },
      score: scoreGlobalSearchMatch({
        query,
        candidates: [organization.name],
      }),
    })),
    limit,
  );
};

// NOTE: NSAI-6608 — an entry's deep-link handle. FAQ_ITEM_IDS only covers the
// entries something links to, so an entry without one falls back to its
// question: that is stable for as long as the language is, which is exactly
// the life of a palette result.
export const getFaqItemDeepLinkId = (item: IFaqItem): string =>
  item.id ?? item.question;

export const findFaqItemByDeepLinkId = ({
  categories,
  itemId,
}: IFindFaqItemByDeepLinkIdProps): IFaqItem | null =>
  categories
    .flatMap((category) => category.items)
    .find((item) => getFaqItemDeepLinkId(item) === itemId) ?? null;

// NOTE: NSAI-6608 — FAQ entries, matched on the question first and on the
// answer summary second, so a question wording wins over a passing mention.
export const buildGlobalSearchFaqItems = ({
  query,
  categories,
  limit,
}: IBuildGlobalSearchFaqItemsProps): IGlobalSearchItem[] => {
  if (!isQuerySearchable(query)) return [];

  return rankItems(
    categories.flatMap((category) =>
      category.items.map((faqItem) => ({
        item: {
          type: EGlobalSearchItemType.FAQ_ITEM,
          id: getFaqItemDeepLinkId(faqItem),
          title: faqItem.question,
          subtitle: category.category,
          params: {},
        },
        score: scoreGlobalSearchMatch({
          query,
          candidates: [faqItem.question, faqItem.answer.summary],
        }),
      })),
    ),
    limit,
  );
};

// NOTE: NSAI-6589 — the connector catalog the Integrations page offers. The
// caller passes only providers the deployment enables for this project.
export const buildGlobalSearchIntegrationProviderItems = ({
  query,
  providers,
  limit,
}: IBuildGlobalSearchIntegrationProviderItemsProps): IGlobalSearchItem[] => {
  if (!isQuerySearchable(query)) return [];

  return rankItems(
    providers.map((provider) => ({
      item: {
        type: EGlobalSearchItemType.INTEGRATION_PROVIDER,
        id: provider.id,
        title: provider.name,
        subtitle: provider.description,
        params: {},
      },
      score: scoreGlobalSearchMatch({
        query,
        candidates: [provider.name, provider.description],
      }),
    })),
    limit,
  );
};

// NOTE: NSAI-6589 — fallbacks offered for any searchable query, after every
// real result: look the text up in the activity history, or in the docs.
export const buildGlobalSearchActionItems = ({
  query,
  labels,
  isDocsAvailable,
}: IBuildGlobalSearchActionItemsProps): IGlobalSearchItem[] => {
  const trimmedQuery = query.trim();
  if (!isQuerySearchable(trimmedQuery)) return [];

  const actions: IGlobalSearchItem[] = [
    {
      type: EGlobalSearchItemType.ACTION,
      id: "action.search_history",
      title: labels.searchHistory,
      subtitle: null,
      params: {
        targetKind: EGlobalSearchTargetKind.SEARCH_HISTORY,
        query: trimmedQuery,
      },
    },
  ];

  if (isDocsAvailable) {
    actions.push({
      type: EGlobalSearchItemType.ACTION,
      id: "action.search_docs",
      title: labels.searchDocs,
      subtitle: null,
      params: {
        targetKind: EGlobalSearchTargetKind.SEARCH_DOCS,
        query: trimmedQuery,
      },
    });
  }

  return actions;
};
